import React, { useState } from 'react';
import {
  Box,
  Stack,
  useMediaQuery,
  useTheme,
  Typography,
  Chip,
  Menu,
  MenuItem,
  ListItemText,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { getRepairStatusColors, getRepairStatusFill } from '../../utils/repairStatusColors';
import { REPAIR_STATUS_CUSTOMER_LABELS } from '../../utils/repairStatusDisplay';
import { formatStatusDateParts } from '../../utils/repairStatusDates';

interface StatusStepperProps {
  status: string;
  onChange: (newStatus: string) => void;
  disabled?: boolean;
  /** תצוגה קומפקטית - שורה אחת עם מעגלים קטנים */
  compact?: boolean;
  /** מובייל — צ'יפ סטטוס + תפריט בחירה */
  chipPicker?: boolean;
  /** תאריך/שעה לכל שלב (ISO) — מוצג מתחת לתווית בשלבים שהושגו */
  statusDates?: Partial<Record<string, string>>;
  /** ניסוח ברור/מקצועי ללקוח במקום התוויות התפעוליות הקצרות (עמוד מעקב ציבורי בלבד) */
  customerFacing?: boolean;
}

const steps = [
  'התקבל, ממתין לטיפול',
  'ממתין לאישור לקוח',
  'סיום טיפול טכנאי',
  'ממתין למשלוח חזרה',
  'חזר ללקוח',
];

/** תוויות תצוגה לשלבים – כותרות קצרות בראש */
const stepLabels: Record<string, string> = {
  'התקבל, ממתין לטיפול': 'ממתין לטיפול',
  'ממתין לאישור לקוח': 'ממתין לאישור לקוח',
  'סיום טיפול טכנאי': 'סיום טיפול טכנאי',
  'ממתין למשלוח חזרה': 'ממתין למשלוח',
  'חזר ללקוח': 'חזר ללקוח',
};

/** תווית קצרה על ציר ההתקדמות (מובייל) — מימין לשמאל */
const stepAxisLabels: Record<string, string> = {
  'התקבל, ממתין לטיפול': 'נקלט',
  'ממתין לאישור לקוח': 'שיחות',
  'סיום טיפול טכנאי': 'טכנאי',
  'ממתין למשלוח חזרה': 'משלוח',
  'חזר ללקוח': 'חזר',
};


const normalizeStatus = (status: string) => {
  if (status === 'בוטל באותו היום') return '';
  if (status === 'התקבל במעבדה' || status === 'ממתין לטיפול') {
    return 'התקבל, ממתין לטיפול';
  }
  return status;
};

/** מירכוז אופטי של ספרה בתוך עיגול (grid + LTR כדי שהספרה לא תזוז ב-RTL) */
const circleCenterSx = {
  display: 'grid',
  placeItems: 'center',
  lineHeight: 0,
  boxSizing: 'border-box' as const,
  p: 0,
  m: 0,
  overflow: 'hidden',
  direction: 'ltr' as const,
};

function StepCircleNumber({
  n,
  fontSize,
}: {
  n: number;
  fontSize: string | number;
}) {
  return (
    <Box
      component="span"
      sx={{
        display: 'block',
        lineHeight: 1,
        fontSize,
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        // ספרות רבות נראות גבוהות מדי ב־flex/grid — תיקון אופטי קל
        transform: 'translateY(0.06em)',
        userSelect: 'none',
      }}
    >
      {n}
    </Box>
  );
}

function StepDateCaption({
  iso,
  isActive,
  compact,
}: {
  iso?: string;
  isActive: boolean;
  compact?: boolean;
}) {
  const theme = useTheme();
  const parts = formatStatusDateParts(iso);
  if (!parts) return null;
  const dark = theme.palette.mode === 'dark';
  return (
    <Typography
      component="span"
      title={`${parts.date} ${parts.time}`.trim()}
      sx={{
        display: 'block',
        width: '100%',
        textAlign: 'center',
        direction: 'ltr',
        unicodeBidi: 'embed',
        fontSize: compact ? '0.5rem' : '0.625rem',
        lineHeight: 1.25,
        fontWeight: isActive ? 700 : 600,
        color: isActive
          ? 'inherit'
          : dark
            ? 'rgba(255,255,255,0.72)'
            : 'text.secondary',
        letterSpacing: '0.02em',
        mt: 0.15,
      }}
    >
      {parts.date}
      {parts.time ? (
        <Box component="span" sx={{ display: 'block', opacity: 0.92 }}>
          {parts.time}
        </Box>
      ) : null}
    </Typography>
  );
}

const StatusStepper: React.FC<StatusStepperProps> = ({
  status,
  onChange,
  disabled = false,
  compact = false,
  chipPicker = false,
  statusDates,
  customerFacing = false,
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const statusColorMode = theme.palette.mode === 'dark' ? 'dark' : 'light';
  const pendingCircleBg = statusColorMode === 'dark' ? '#52525b' : '#e0e0e0';
  const arrowColor = statusColorMode === 'dark' ? '#a1a1aa' : '#bdbdbd';
  const inactiveLabelColor = statusColorMode === 'dark' ? 'rgba(255,255,255,0.45)' : undefined;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isNarrow = useMediaQuery(theme.breakpoints.down('sm'));
  const isTiny = useMediaQuery(theme.breakpoints.down(400));
  const isCompactRow = useMediaQuery(theme.breakpoints.down('lg'));
  
  const normalizedStatus = normalizeStatus(status);
  const stepIndex = steps.indexOf(normalizedStatus);
  /** אין סטטוס ריק / לא מוכר – בלי הדגשת שלב (לא מסמנים "מעבדה" כברירת מחדל) */
  const activeStep = stepIndex >= 0 ? stepIndex : -1;
  // במובייל: מידות קטנות ככל האפשר. isTiny = מסך מאוד צר (~400px)
  const circleSize = isTiny ? 20 : (isNarrow ? 24 : (isMobile ? 28 : (isCompactRow ? 32 : 36)));
  const circleFontSize = isTiny ? '0.65rem' : (isNarrow ? '0.7rem' : (isMobile ? '0.75rem' : (isCompactRow ? '0.9rem' : '1.05rem')));
  const labelFontSize = isTiny ? '0.5rem' : (isNarrow ? '0.55rem' : (isMobile ? '0.65rem' : (isCompactRow ? '0.7rem' : '0.8rem')));
  const arrowBorder = isTiny ? 4 : (isNarrow ? 5 : (isMobile ? 6 : (isCompactRow ? 6 : 8)));
  const arrowRight = isTiny ? 6 : (isNarrow ? 7 : (isMobile ? 8 : (isCompactRow ? 9 : 12)));
  const compactCircleSize = isTiny ? 22 : (isNarrow ? 24 : 28);
  const compactCircleFontSize = isTiny ? '0.72rem' : (isNarrow ? '0.78rem' : '0.85rem');

  if (chipPicker) {
    const displayStatus = normalizedStatus || status;
    const displayLabel =
      (displayStatus && (stepLabels[displayStatus] ?? displayStatus)) || 'בחר סטטוס';
    const colors = getRepairStatusColors(
      steps.includes(displayStatus) ? displayStatus : steps[0],
      statusColorMode,
    );
    return (
      <Stack spacing={0.75} sx={{ width: '100%', minWidth: 0, alignItems: 'stretch' }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
          <Chip
            label={displayLabel}
            size="small"
            disabled={disabled}
            icon={<KeyboardArrowDownIcon sx={{ fontSize: '1rem !important' }} />}
            onClick={(e) => {
              if (!disabled) setMenuAnchor(e.currentTarget);
            }}
            sx={{
              maxWidth: 'min(100%, 320px)',
              height: 32,
              fontWeight: 700,
              fontSize: '0.75rem',
              border: '2px solid',
              borderColor: colors.text,
              bgcolor: colors.bg,
              color: colors.text,
              flexDirection: 'row-reverse',
              '& .MuiChip-label': { px: 1, overflow: 'hidden', textOverflow: 'ellipsis' },
              '& .MuiChip-icon': { color: colors.text, ml: 0.25, mr: -0.25 },
            }}
          />
        </Box>
        <Box sx={{ width: '100%', minWidth: 0, px: 0.25, direction: 'rtl' }}>
          {/* נקודות + קווים — התקדמות מימין (נקלט) לשמאל (חזר ללקוח) */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              minWidth: 0,
            }}
          >
            {steps.map((label, index) => {
              const stepColors = getRepairStatusColors(label, statusColorMode);
              const prevColors =
                index > 0 ? getRepairStatusColors(steps[index - 1], statusColorMode) : stepColors;
              const isActive = index === activeStep;
              const isCompleted = activeStep >= 0 && index < activeStep;
              const isReached = isActive || isCompleted;
              const segmentFilled = activeStep >= 0 && index > 0 && index <= activeStep;
              return (
                <React.Fragment key={label}>
                  {index > 0 && (
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 4,
                        height: 3,
                        borderRadius: 1,
                        bgcolor: segmentFilled
                          ? getRepairStatusFill(prevColors)
                          : pendingCircleBg,
                        opacity: segmentFilled ? 1 : 0.5,
                        transition: 'background-color 0.2s ease',
                      }}
                    />
                  )}
                  <Box
                    title={stepLabels[label] ?? label}
                    sx={{
                      width: isActive ? 12 : 9,
                      height: isActive ? 12 : 9,
                      borderRadius: '50%',
                      flexShrink: 0,
                      bgcolor: isReached ? getRepairStatusFill(stepColors) : pendingCircleBg,
                      boxShadow: isActive
                        ? `0 0 0 2px ${stepColors.bg}, 0 0 0 3px ${getRepairStatusFill(stepColors)}`
                        : 'none',
                      transition: 'all 0.2s ease',
                    }}
                  />
                </React.Fragment>
              );
            })}
          </Box>
          {/* תוויות שלבים מתחת לנקודות */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              width: '100%',
              minWidth: 0,
              mt: 0.45,
            }}
          >
            {steps.map((label, index) => {
              const stepColors = getRepairStatusColors(label, statusColorMode);
              const isActive = index === activeStep;
              const isCompleted = activeStep >= 0 && index < activeStep;
              const isReached = isActive || isCompleted;
              return (
                <React.Fragment key={`${label}-axis`}>
                  {index > 0 && <Box sx={{ flex: 1, minWidth: 4 }} />}
                  <Box sx={{ flexShrink: 0, width: isActive ? 52 : 44, minWidth: 0 }}>
                    <Typography
                      variant="caption"
                      component="span"
                      title={stepLabels[label] ?? label}
                      sx={{
                        display: 'block',
                        textAlign: 'center',
                        fontSize: isTiny ? '0.52rem' : '0.58rem',
                        lineHeight: 1.15,
                        fontWeight: isActive ? 800 : isCompleted ? 600 : 500,
                        color: isActive || isCompleted ? stepColors.text : 'text.secondary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {stepAxisLabels[label] ?? stepLabels[label]}
                    </Typography>
                    {isReached && (
                      <StepDateCaption iso={statusDates?.[label]} isActive={isActive} compact />
                    )}
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
        </Box>
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={() => setMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          slotProps={{ paper: { sx: { direction: 'rtl', minWidth: 220, maxWidth: '92vw' } } }}
        >
          {steps.map((step) => {
            const stepColors = getRepairStatusColors(step, statusColorMode);
            const selected = step === normalizedStatus;
            return (
              <MenuItem
                key={step}
                selected={selected}
                onClick={() => {
                  setMenuAnchor(null);
                  if (!disabled) onChange(step);
                }}
                sx={selected ? { bgcolor: stepColors.bg } : undefined}
              >
                <ListItemText
                  primary={stepLabels[step] ?? step}
                  primaryTypographyProps={{
                    fontSize: '0.85rem',
                    fontWeight: selected ? 700 : 500,
                    color: stepColors.text,
                  }}
                />
              </MenuItem>
            );
          })}
        </Menu>
      </Stack>
    );
  }

  if (compact) {
    const compactLabelFor = (label: string) =>
      (customerFacing ? REPAIR_STATUS_CUSTOMER_LABELS[label] : stepLabels[label]) ?? label;

    return (
      <Box sx={{ width: '100%', minWidth: 0, direction: 'rtl' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
            minWidth: 0,
            py: 0.25,
          }}
        >
          {steps.map((label, index) => {
            const colors = getRepairStatusColors(label, statusColorMode);
            const isActive = index === activeStep;
            const isCompleted = activeStep >= 0 && index < activeStep;
            const isReached = isActive || isCompleted;
            const size = isActive ? compactCircleSize + 6 : compactCircleSize;
            const dateParts =
              isReached ? formatStatusDateParts(statusDates?.[label]) : null;

            return (
              <React.Fragment key={label}>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 0.15,
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      display: 'flex',
                      justifyContent: 'center',
                      // ה-ring של השלב הפעיל יוצא מהמעגל — מרווחים כדי שלא יידבק לטקסט
                      pb: isActive ? 1 : 0.5,
                    }}
                  >
                  <Box
                    title={compactLabelFor(label)}
                    sx={{
                      width: size,
                      height: size,
                      borderRadius: '50%',
                      backgroundColor: isActive
                        ? getRepairStatusFill(colors)
                        : isCompleted
                          ? colors.bg
                          : pendingCircleBg,
                      color: isActive ? '#fff' : isCompleted ? colors.text : '#fff',
                      ...circleCenterSx,
                      fontWeight: 'bold',
                      cursor: disabled ? 'default' : 'pointer',
                      transition: 'all 0.2s ease',
                      border: isActive || isCompleted ? '2px solid' : 'none',
                      borderColor:
                        isActive || isCompleted ? getRepairStatusFill(colors) : 'transparent',
                      boxShadow: isActive
                        ? `0 0 0 3px ${colors.bg}, 0 0 0 5px ${getRepairStatusFill(colors)}, 0 3px 10px ${getRepairStatusFill(colors)}66`
                        : isCompleted
                          ? `0 0 0 1px ${getRepairStatusFill(colors)}44`
                          : 'none',
                      flexShrink: 0,
                      zIndex: isActive ? 2 : 1,
                      transform: isActive ? 'scale(1.06)' : 'none',
                      '&:hover': {
                        transform: disabled ? (isActive ? 'scale(1.06)' : 'none') : isActive ? 'scale(1.1)' : 'scale(1.08)',
                      },
                    }}
                    onClick={() => !disabled && onChange(label)}
                  >
                    <StepCircleNumber n={index + 1} fontSize={compactCircleFontSize} />
                  </Box>
                  </Box>
                  <Typography
                    component="span"
                    title={compactLabelFor(label)}
                    sx={{
                      width: '100%',
                      fontFamily: 'inherit',
                      fontSize: isActive ? (isTiny ? '0.625rem' : '0.75rem') : isTiny ? '0.6rem' : '0.6875rem',
                      textAlign: 'center',
                      lineHeight: 1.35,
                      fontWeight: isActive ? 600 : isCompleted ? 500 : 400,
                      letterSpacing: 'normal',
                      fontStyle: 'normal',
                      color:
                        isActive || isCompleted
                          ? colors.text
                          : inactiveLabelColor || 'text.secondary',
                      display: 'block',
                      px: isActive ? 0.35 : 0.1,
                      py: isActive ? 0.25 : 0,
                      mt: 0.25,
                      borderRadius: isActive ? 1 : 0,
                      bgcolor: isActive ? colors.bg : 'transparent',
                      border: isActive ? `1px solid ${getRepairStatusFill(colors)}66` : 'none',
                      boxShadow: isActive ? `0 1px 4px ${getRepairStatusFill(colors)}33` : 'none',
                      whiteSpace: 'normal',
                      WebkitFontSmoothing: 'antialiased',
                    }}
                  >
                    {compactLabelFor(label)}
                  </Typography>
                  {dateParts && (
                    <StepDateCaption iso={statusDates?.[label]} isActive={isActive} />
                  )}
                </Box>
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      width: isTiny ? 10 : 14,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      alignSelf: 'flex-start',
                      mt: `${Math.max(0, compactCircleSize / 2 - (isTiny ? 4 : 5))}px`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 0,
                        height: 0,
                        borderTop: `${isTiny ? 4 : 5}px solid transparent`,
                        borderBottom: `${isTiny ? 4 : 5}px solid transparent`,
                        borderRight: `${isTiny ? 6 : 8}px solid ${
                          activeStep >= 0 && index < activeStep
                            ? getRepairStatusFill(
                                getRepairStatusColors(steps[index], statusColorMode),
                              )
                            : arrowColor
                        }`,
                        opacity: activeStep >= 0 && index < activeStep ? 1 : 0.65,
                      }}
                    />
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    );
  }

  // דסקטופ: כל שלב בעמודה אחת (עיגול + תווית + תאריך) כדי שלא יזוז מהעיגול
  if (!isMobile) {
    return (
      <Box sx={{ width: '100%', minWidth: 0, direction: 'rtl' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            width: '100%',
            minWidth: 0,
          }}
        >
          {steps.map((label, index) => {
            const colors = getRepairStatusColors(label, statusColorMode);
            const isActive = index === activeStep;
            const isCompleted = index < activeStep;
            const isReached = isActive || isCompleted;
            const size = isActive ? circleSize + 4 : circleSize;
            return (
              <React.Fragment key={label}>
                <Box
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.15,
                  }}
                >
                  <Box
                    title={stepLabels[label] ?? label}
                    sx={{
                      width: size,
                      height: size,
                      borderRadius: '50%',
                      backgroundColor: isActive
                        ? getRepairStatusFill(colors)
                        : isCompleted
                          ? colors.bg
                          : pendingCircleBg,
                      color: isActive ? '#fff' : isCompleted ? colors.text : '#fff',
                      ...circleCenterSx,
                      fontWeight: 'bold',
                      cursor: disabled ? 'default' : 'pointer',
                      transition: 'all 0.3s ease',
                      border: isActive || isCompleted ? '2px solid' : 'none',
                      borderColor:
                        isActive || isCompleted ? getRepairStatusFill(colors) : 'transparent',
                      boxShadow: isActive
                        ? `0 0 0 2px ${getRepairStatusFill(colors)}44`
                        : 'none',
                      flexShrink: 0,
                      '&:hover': { transform: disabled ? 'none' : 'scale(1.05)' },
                    }}
                    onClick={() => !disabled && onChange(label)}
                  >
                    <StepCircleNumber n={index + 1} fontSize={circleFontSize} />
                  </Box>
                  <Typography
                    variant="caption"
                    sx={{
                      width: '100%',
                      fontSize: labelFontSize,
                      textAlign: 'center',
                      lineHeight: 1.25,
                      fontWeight: isActive ? 'bold' : 'normal',
                      color: isActive || isCompleted ? colors.text : 'text.secondary',
                    }}
                  >
                    {stepLabels[label] ?? label}
                  </Typography>
                  {isReached && (
                    <Box sx={{ color: colors.text, width: '100%' }}>
                      <StepDateCaption iso={statusDates?.[label]} isActive={isActive} />
                    </Box>
                  )}
                </Box>
                {index < steps.length - 1 && (
                  <Box
                    sx={{
                      width: isTiny ? 10 : 14,
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      alignSelf: 'flex-start',
                      mt: `${Math.max(0, size / 2 - (isTiny ? 4 : 6))}px`,
                    }}
                  >
                    <Box
                      sx={{
                        width: 0,
                        height: 0,
                        borderTop: `${arrowBorder}px solid transparent`,
                        borderBottom: `${arrowBorder}px solid transparent`,
                        borderRight: `${arrowRight}px solid ${
                          activeStep >= 0 && index < activeStep
                            ? getRepairStatusFill(
                                getRepairStatusColors(steps[index], statusColorMode),
                              )
                            : arrowColor
                        }`,
                        opacity: activeStep >= 0 && index < activeStep ? 1 : 0.7,
                      }}
                    />
                  </Box>
                )}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>
    );
  }

  // מובייל: עמודות (כל שלב מתחת לקודם)
  return (
    <Stack direction="column" alignItems="stretch" spacing={isTiny ? 0 : (isNarrow ? 0.25 : 0.5)} sx={{ width: '100%' }}>
      <Box sx={{ flex: 1, overflow: 'hidden', width: '100%', pt: isTiny ? 0 : (isNarrow ? 0.25 : 0.5) }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
          {steps.map((label, index) => (
            <React.Fragment key={label}>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0 }}>
                {(() => {
                  const colors = getRepairStatusColors(label, statusColorMode);
                  const isActive = index === activeStep;
                  const isCompleted = index < activeStep;
                  return (
                <Box
                  sx={{
                    width: circleSize + 'px',
                    height: circleSize + 'px',
                    borderRadius: '50%',
                    backgroundColor: isActive
                      ? getRepairStatusFill(colors)
                      : isCompleted
                        ? colors.bg
                        : pendingCircleBg,
                    color: isActive ? '#fff' : isCompleted ? colors.text : '#fff',
                    ...circleCenterSx,
                    fontWeight: 'bold',
                    mb: isTiny ? 0.1 : isNarrow ? 0.15 : 0.25,
                    cursor: disabled ? 'default' : 'pointer',
                    transition: 'all 0.3s ease',
                    border: isActive || isCompleted ? '2px solid' : 'none',
                    borderColor:
                      isActive || isCompleted ? getRepairStatusFill(colors) : 'transparent',
                    flexShrink: 0,
                    '&:hover': { transform: disabled ? 'none' : 'scale(1.05)' },
                  }}
                  onClick={() => !disabled && onChange(label)}
                >
                  <StepCircleNumber n={index + 1} fontSize={circleFontSize} />
                </Box>
                  );
                })()}
                {(() => {
                  const colors = getRepairStatusColors(label, statusColorMode);
                  const isActive = index === activeStep;
                  const isCompleted = index < activeStep;
                  const isReached = isActive || isCompleted;
                  return (
                    <>
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: labelFontSize,
                          textAlign: 'center',
                          lineHeight: 1.2,
                          px: isTiny ? 0 : isNarrow ? 0.25 : 0.5,
                          fontWeight: isActive ? 'bold' : 'normal',
                          color:
                            isActive || isCompleted
                              ? colors.text
                              : inactiveLabelColor || 'text.secondary',
                        }}
                      >
                        {stepLabels[label] ?? label}
                      </Typography>
                      {isReached && (
                        <Box sx={{ color: colors.text, width: '100%' }}>
                          <StepDateCaption iso={statusDates?.[label]} isActive={isActive} compact={isTiny} />
                        </Box>
                      )}
                    </>
                  );
                })()}
              </Box>
              {index < steps.length - 1 && (
                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: isTiny ? 8 : (isNarrow ? 12 : 20), mt: isTiny ? 0.2 : (isNarrow ? 0.35 : 0.5) }}>
                  <Box sx={{ width: 0, height: 0, borderTop: `${arrowBorder}px solid transparent`, borderBottom: `${arrowBorder}px solid transparent`, borderRight: `${arrowRight}px solid ${arrowColor}` }} />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Stack>
  );
};

export default StatusStepper;


